import React, { useState, useEffect } from 'react';
import { Mail, Send, Users, CheckCircle, AlertCircle, Loader2, Info } from 'lucide-react';
import api from '../../api/api';

const SendEmailStaff = () => {
  const [formData, setFormData] = useState({
    recipients: 'all',
    department: '',
    staffType: '',
    subject: '',
    message: ''
  });
  
  const [departments, setDepartments] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [emailConfigured, setEmailConfigured] = useState(false);

  useEffect(() => {
    fetchMasterData();
    checkEmailConfig();
  }, []);

  useEffect(() => {
    fetchRecipientCount();
  }, [formData.recipients, formData.department, formData.staffType]);

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-settings/departments');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const checkEmailConfig = async () => {
    try {
      const response = await api.get('/email-settings/');
      setEmailConfigured(response.data.configured || response.data.service_type === 'default');
    } catch (error) {
      console.error('Error checking email config:', error);
    }
  };

  const fetchRecipientCount = async () => {
    setLoading(true);
    try {
      let filterType = 'all';
      let filterValue = '';
      
      if (formData.recipients === 'department' && formData.department) {
        filterType = 'department';
        filterValue = formData.department;
      } else if (formData.recipients === 'teaching') {
        filterType = 'teaching';
      } else if (formData.recipients === 'non-teaching') {
        filterType = 'non-teaching';
      }
      
      const response = await api.get(`/communication/recipients/staff?filter_type=${filterType}&filter_value=${filterValue}`);
      setRecipientCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching recipient count:', error);
      setRecipientCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim()) {
      setMessage({ type: 'error', text: 'Please enter email subject' });
      return;
    }
    
    if (!formData.message.trim()) {
      setMessage({ type: 'error', text: 'Please enter email message' });
      return;
    }
    
    if (recipientCount === 0) {
      setMessage({ type: 'error', text: 'No recipients found with valid email addresses' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      let filterType = 'all';
      let filterValue = null;
      
      if (formData.recipients === 'department' && formData.department) {
        filterType = 'department';
        filterValue = formData.department;
      } else if (formData.recipients === 'teaching') {
        filterType = 'teaching';
      } else if (formData.recipients === 'non-teaching') {
        filterType = 'non-teaching';
      }

      const response = await api.post('/communication/send-email', {
        recipient_type: 'staff',
        filter_type: filterType,
        filter_value: filterValue,
        subject: formData.subject,
        message: formData.message
      });

      setMessage({ 
        type: 'success', 
        text: `✅ Email sending started! ${response.data.recipient_count} staff members will receive the email.` 
      });
      
      // Reset form
      setFormData({
        ...formData,
        subject: '',
        message: ''
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send emails. Please try again.' 
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Send Email to Staff</h1>
        <p className="text-sm text-gray-500 mt-1">Compose and send emails to staff members using your configured email settings</p>
      </div>

      {!emailConfigured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Email not configured</p>
            <p className="text-sm text-yellow-700 mt-1">
              Please configure your email settings first. Go to Settings → Email Settings to set up.
            </p>
          </div>
        </div>
      )}

      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Recipients Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="inline h-4 w-4 mr-2" />
                  Select Recipients
                </label>
                <select
                  value={formData.recipients}
                  onChange={(e) => setFormData({ ...formData, recipients: e.target.value, department: '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Staff</option>
                  <option value="department">By Department</option>
                  <option value="teaching">Teaching Staff Only</option>
                  <option value="non-teaching">Non-Teaching Staff Only</option>
                </select>
              </div>

              {/* Conditional Filters */}
              {formData.recipients === 'department' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Department</label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choose a department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="inline h-4 w-4 mr-2" />
                  Email Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Enter email subject"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Type your message here..."
                  rows={8}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">The message will be formatted as HTML email automatically.</p>
              </div>

              {/* Send Button */}
              <button
                type="submit"
                disabled={sending || recipientCount === 0}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Send Email to {recipientCount} Staff
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Recipient Count Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Recipients</h3>
            <div className="text-center py-4">
              {loading ? (
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
              ) : (
                <>
                  <p className="text-4xl font-bold text-blue-600">{recipientCount}</p>
                  <p className="text-sm text-gray-500 mt-1">staff with valid email</p>
                </>
              )}
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">How it works:</p>
                <ul className="mt-2 space-y-1 text-blue-700">
                  <li>• Emails are sent using your configured email settings</li>
                  <li>• Only staff with valid email addresses will receive</li>
                  <li>• Email sending happens in background</li>
                  <li>• Check Email Logs for delivery status</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmailStaff;
